import { searchWithBoundingBoxQuery } from '@api/graphql/searchWithBoundingBox';
import axios from '@axios';
import { useAppSelector } from '@redux/hooks';
import type { SearchResponse } from '@typings/api';
import type { BoundingBox, SortType } from '@typings/search';
import { generateSeed } from '@utils/generateSeed';
import { Logger } from '@utils/logger/logger';
import sortParam from '@utils/sortParam';
import { useAtomValue } from 'jotai';
import queryString from 'query-string';
import { searchQueryParamsAtom } from '../pages/VendorsSearch/containers/FilterPills/atoms';
import { MAX_VENDORS } from '../pages/VendorsSearch/containers/MapView/constants';
import type { Bounds } from '../pages/VendorsSearch/containers/MapView/utils/usStateBounds';
import { getLocationFromSlug } from '../pages/VendorsSearch/utils';

export type Match = {
	params: {
		facet: string;
		locationSlug: string;
	};
};
type SetFilteredVendors = (vendors: Vendor.Raw[]) => void;
type TranslateBoundingBox = (bbox: Bounds) => BoundingBox;
type SearchByBoundingBox = (
	bbox: Bounds,
	setFilteredVendors: SetFilteredVendors,
) => void;

type SearchVariables = {
	boundingBox: BoundingBox;
	categoryGuid?: string;
	city?: string;
	filters?: string[];
	page?: number;
	sort?: SortType;
	state?: string;
};

const getSearchResults = async (
	setFilteredVendor: SetFilteredVendors,
	searchVariables: SearchVariables,
) => {
	const seed = generateSeed();
	const data = {
		query: searchWithBoundingBoxQuery(),
		variables: {
			...searchVariables,
			seed,
		},
	};

	try {
		const searchResponse = await axios.post<SearchResponse>(data);
		if (searchResponse?.data?.data?.search?.profiles) {
			const maxAllowedVendors = searchResponse.data.data.search.profiles.slice(
				0,
				MAX_VENDORS,
			);
			setFilteredVendor(maxAllowedVendors);
		}
	} catch (error) {
		Logger.error({
			log_message: 'Error searching with bounding box',
			errorToLog: error,
			newRelic: true,
		});
	}
};

const translateBoundingBox: TranslateBoundingBox = (bbox) => {
	return {
		topLeft: {
			latitude: bbox.north,
			longitude: bbox.west,
		},
		bottomRight: {
			latitude: bbox.south,
			longitude: bbox.east,
		},
	};
};

export const useSearchByBoundingBox = (
	match: Match,
	searchLocation: string,
	isCleared: boolean,
) => {
	const userLocation = getLocationFromSlug(match.params.locationSlug);
	const category = useAppSelector((state) => state.category);
	const { filters } = useAtomValue(searchQueryParamsAtom);
	const page = useAppSelector((state) => state.search.pagination.page);
	const zeroIndexBasedPage = page > 0 ? page - 1 : 0;
	const { sort } = queryString.parse(searchLocation);

	const searchOptions: Partial<SearchVariables> = {
		categoryGuid: category.id || '',
		city: userLocation.city || '',
		filters: isCleared ? [category.id] : filters,
		page: zeroIndexBasedPage,
		sort: sortParam(sort),
		state: userLocation.stateCode || '',
	};

	const searchByBoundingBox: SearchByBoundingBox = (
		bbox,
		setFilteredVendors,
	) => {
		const boundingBox = translateBoundingBox(bbox);
		getSearchResults(setFilteredVendors, { ...searchOptions, boundingBox });
	};

	return { searchByBoundingBox };
};
